// Import dependencies
import Swiper from 'swiper';
import { gsap } from 'gsap';
import Splitting from 'splitting';
import initialiser from '../../utils/initialiser';

// Import utils
import { addClass, resizeFont } from '../../utils';

const defaultSwiperOptions = {
  effect: 'fade',
  keyboardControl: true,
  loop: true,
  mousewheelControl: true,
  speed: 500,
  navigation: {
    enabled: true,
    nextEl: '.js-hero-home-btn-next',
  },
};

const defaultSettings = {
  activeClass: 'is-active',
};

class HeroHomeCarousel {
  constructor(config) {
    const { el, settings } = config;

    // Set module dom elements
    this.container = el;
    this.swiperEnabled = false;
    /** @type {Swiper} */
    this.swiperInstance = null;
    this.heroContainer = el.querySelector(settings.heroContainer);
    this.buttonNext = this.heroContainer.querySelector(settings.buttonNext);
    this.actionWrapper = this.heroContainer.querySelector(settings.actionWrapper);
    this.nextPerformanceDetails = this.heroContainer.querySelector(settings.nextPerformanceDetails);
    this.nextPerformanceTitle = this.heroContainer.querySelector(settings.nextPerformanceTitle);
    this.nextPerformanceImg = this.heroContainer.querySelector(settings.nextPerformanceImg);
    this.contentWrapper = settings.contentWrapper;
    this.contentWrapperMobile = settings.contentWrapperMobile;
    this.content = settings.content;
    this.contentImg = settings.contentImg;
    this.contentImgContainer = settings.contentImgContainer;
    this.revalidateLazyImages = settings.revalidateLazyImages;
    this.activeSlide = settings.activeSlide;
    this.nextSlide = settings.nextSlide;
    this.previousSlide = settings.previousSlide;
    this.title = settings.title;
    this.hiddenEles = settings.hiddenEles;
    this.mediaQueryMedium = window.matchMedia(`(min-width: ${settings.mediaQueryMedium}px)`).matches;
    this.mediaQuerySmall = window.matchMedia(`(min-width: ${settings.mediaQuerySmall}px)`).matches;
    this.noSwipingClass = 'swiper-no-swiping';
    this.customSwiperOptions = {
      noSwiping: this.mediaQueryMedium,
      noSwipingClass: this.noSwipingClass,
    };

    // Set settings
    this.settings = { ...defaultSettings, ...settings };
  }

  // Initialisation Function
  init = () => {
    if (!this.container || !this.heroContainer) return;

    // If swiper is already enabled
    if (this.swiperEnabled) return;
    this.initSwiper();
    this.bindEvents();
  };

  bindEvents = () => {
    // On load
    let activeSlide = this.heroContainer.querySelector(this.activeSlide);
    let activeSlideIndex = this.swiperInstance.realIndex;
    let nextSlideIndex = activeSlideIndex + 2;
    let nextSlide = this.swiperInstance.slides[nextSlideIndex];

    this.handleAnimations(activeSlide, nextSlide);
    this.handleIosViewportHeight();

    // Disable swiping on desktop
    this.disableSwiping();

    window.addEventListener('resize', () => {
      this.handleIosViewportHeight();
    });

    this.swiperInstance.on('slidePrevTransitionStart', () => {
      activeSlide = this.heroContainer.querySelector(this.activeSlide);
      nextSlide = this.heroContainer.querySelector(this.nextSlide);
      this.handleAnimations(activeSlide, nextSlide);
    });

    this.swiperInstance.on('slideNextTransitionStart', () => {
      activeSlide = this.heroContainer.querySelector(this.activeSlide);
      activeSlideIndex = this.swiperInstance.realIndex;
      nextSlideIndex = activeSlideIndex + 2;
      nextSlide = this.swiperInstance.slides[nextSlideIndex];
      this.handleAnimations(activeSlide, nextSlide);
    });

    this.buttonNext.addEventListener('click', () => {
      const swiper = this.swiperInstance;
      const next = (swiper.previousIndex + 1) % swiper.slides.length;

      swiper.animating = false;
      swiper.slideTo(next, 150, true);
    });
  };

  initSwiper = () => {
    const options = {
      ...defaultSwiperOptions,
      ...this.customSwiperOptions,
    };

    this.swiperEnabled = true;
    this.swiperInstance = new Swiper(this.heroContainer, options);
    this.heroContainer.classList.add('has-carousel');
  };

  handleAnimations = (activeSlide, nextSlide) => {
    if (this.revalidateLazyImages) this.revalidateLazyImages();
    this.updateNextPerformanceTitle(nextSlide);
    this.updateNextPerformanceImage(nextSlide);
    this.animateTitle(activeSlide);
    this.animateBackground(activeSlide);

    setTimeout(() => {
      this.animateImage(activeSlide);
    }, 300);

    setTimeout(() => {
      this.animateSecondaryContent(activeSlide);
    }, 600);
  };

  // Iphone, Ipad address bar overlapping issue fix
  handleIosViewportHeight = () => {
    if (!this.mediaQueryMedium) {
      const vh = (window.innerHeight * 0.01) + 0.10;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
  };

  removeActiveClass = (eles) => {
    eles.forEach((ele) => {
      if (ele.classList.contains(this.settings.activeClass)) {
        ele.classList.remove(this.settings.activeClass);
      }
    });
  };

  gsapAnimation = (eles, startState, endState) => {
    gsap.fromTo(eles, startState, endState);
  };

  positionActionWrapper = (activeSlide) => {
    if (!this.mediaQueryMedium) {
      const containerHeight = this.heroContainer.offsetHeight;
      const contentWrapperHeight = activeSlide.querySelector(this.contentWrapper).offsetHeight;
      this.actionWrapper.style.height = this.mediaQuerySmall
        ? `${Math.floor(contentWrapperHeight / 5)}px`
        : `${Math.floor(contentWrapperHeight / 3.5)}px`;
      this.actionWrapper.style.bottom = `${containerHeight - contentWrapperHeight}px`;
    }
  };

  // Update next performance title and image in sidebar on right
  updateNextPerformanceTitle = (activeSlide) => {
    const nextPerformanceTitle = activeSlide.dataset.title;
    const eleParent = this.nextPerformanceTitle.parentElement;
    const eleParentTopMargin = parseInt(window.getComputedStyle(eleParent).getPropertyValue('padding-top'), 10);
    const eleParentWidth = parseInt(window.getComputedStyle(eleParent).getPropertyValue('width'), 10);
    const eleHeight = this.buttonNext.offsetHeight - ((eleParentTopMargin * 2) + 35);
    const eleWidth = eleParentWidth - (eleParentTopMargin * 2);

    this.nextPerformanceTitle.classList.remove('is-active');

    setTimeout(() => {
      addClass(this.nextPerformanceTitle, this.settings.activeClass);
      this.nextPerformanceTitle.innerHTML = `<span>${nextPerformanceTitle}</span>`;

      resizeFont(this.nextPerformanceTitle, 14, 24, eleWidth, eleHeight);
    }, 300);
  };

  updateNextPerformanceImage = (activeSlide) => {
    const nextPerformanceImage = activeSlide.dataset.image;

    this.nextPerformanceImg.classList.remove('is-active');

    setTimeout(() => {
      addClass(this.nextPerformanceImg, this.settings.activeClass);
      this.nextPerformanceImg.style.backgroundImage = `url(${nextPerformanceImage})`;
    }, 500);
  };

  animateTitle = (activeSlide) => {
    const content = activeSlide.querySelector(this.content);
    const title = content.querySelector(this.title);
    const titlesInSlider = [...this.heroContainer.querySelectorAll(this.title)];
    const titleWidth = title.offsetWidth;
    const titleHeight = this.mediaQueryMedium ? 200 : 110;
    const minFontSize = this.mediaQueryMedium ? 20 : 18;
    const maxFontSize = this.mediaQueryMedium ? 48 : 30;
    Splitting({ target: title, by: 'chars' });
    resizeFont(title, minFontSize, maxFontSize, titleWidth, titleHeight);

    this.removeActiveClass(titlesInSlider);

    setTimeout(() => {
      addClass(title, this.settings.activeClass);
    }, 500);
  };

  // Animate everything in content except title
  animateSecondaryContent = (activeSlide) => {
    const content = activeSlide.querySelector(this.content);
    const secondaryContentEles = [...content.querySelectorAll(this.hiddenEles)];
    const mobileContent = activeSlide.querySelector(this.contentWrapperMobile);
    const secondaryMobileContentEles = [...mobileContent.querySelectorAll(this.hiddenEles)];
    const secondaryContentElesInSlider = [...this.heroContainer.querySelectorAll(this.hiddenEles)];
    const eleStartState = { opacity: 0, y: 100 };
    const eleEndState = {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'back.inOut(1.7)',
      stagger: {
        duration: 1,
        each: 0.25,
        from: 'start',
      },
    };

    secondaryContentElesInSlider.forEach((ele) => {
      ele.style.opacity = 0;
    });

    this.gsapAnimation(
      this.mediaQueryMedium ? secondaryContentEles : secondaryMobileContentEles,
      eleStartState,
      eleEndState,
    );
  };

  animateBackground = (activeSlide) => {
    const backgroundInSlider = [...this.heroContainer.querySelectorAll(this.contentWrapper)];
    const background = activeSlide.querySelector(this.contentWrapper);

    this.removeActiveClass(backgroundInSlider);

    setTimeout(() => {
      addClass(background, this.settings.activeClass);
    }, 500);
  };

  animateImage = (activeSlide) => {
    const imageWrapper = activeSlide.querySelector(this.contentImg);
    const image = imageWrapper && imageWrapper.querySelector('img');
    const imageWrappersInSlider = [...this.heroContainer.querySelectorAll(this.contentImg)];

    image.addEventListener('load', () => {
      this.positionActionWrapper(activeSlide);
    });

    this.removeActiveClass(imageWrappersInSlider);

    setTimeout(() => {
      addClass(imageWrapper, this.settings.activeClass);
      this.positionActionWrapper(activeSlide);
    }, 300);
  };

  disableSwiping = () => {
    this.swiperInstance.slides.forEach((slide) => {
      if (this.mediaQueryMedium) {
        slide.classList.add(this.noSwipingClass);
      } else {
        slide.classList.remove(this.noSwipingClass);
      }
    });
  };
}

export default (settings) => initialiser(settings, HeroHomeCarousel);
